import { useEffect, useState } from 'react';
import {
  useBuildingsCsvImportMutation,
  useBuildingsCsvTemplateDownloadMutation,
  useOperatorShortQuery,
} from '@common/cache';
import { SelectChangeEvent } from '@mui/material';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

export const useImportBuildingModal = (handleClose: () => void) => {
  const { data: operatorOptions = [] } = useOperatorShortQuery();
  const {
    mutateAsync: templateMutateAsync,
    data: csvTemplate,
    isPending,
  } = useBuildingsCsvTemplateDownloadMutation();
  const {
    mutateAsync: importMutateAsync,
    error,
    reset,
  } = useBuildingsCsvImportMutation();
  const [operatorId, setOperatorId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (csvTemplate) {
      const url = URL.createObjectURL(csvTemplate);

      const a = document.createElement('a');
      a.href = url;
      a.download = `buildings template`;

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [csvTemplate]);

  useEffect(() => {
    if (operatorOptions.length > 0) {
      setOperatorId(operatorOptions[0].id);
    }
  }, [operatorOptions]);

  const handleChangeOperatorId = (event: SelectChangeEvent<string>) => {
    setOperatorId(event.target.value as string);
  };

  const handleChangeFile = (newFile: File | null) => {
    setFile(newFile);
    reset();
  };

  const handleCloseAction = () => {
    handleClose();
    setFile(null);
  };

  const handleDownloadCSVTemplate = async () => {
    const loadingToast = toast.loading('Downloading CSV template...', {
      isLoading: true,
      autoClose: 500,
    });

    try {
      await templateMutateAsync(operatorId);
      toast.success('Downloaded successfully.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleImportCsvFile = async () => {
    const loadingToast = toast.loading('Importing CSV file...', {
      isLoading: true,
      autoClose: 500,
    });

    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await importMutateAsync({
        file: formData,
        record_id: operatorId,
      });
      toast.success('Imported successfully.');
    } catch {
      toast.error('Something went wrong.');
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  return {
    operatorOptions: operatorOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    operatorId,
    handleChangeOperatorId,
    handleDownloadCSVTemplate,
    isTemplateBtnDisabled: isPending,
    handleImportCsvFile,
    handleChangeFile,
    importError: error ? formatApiError(error).split(', ') : [],
    file,
    handleCloseAction,
  };
};
